import {
  Copy,
  LayoutTemplate,
  Mail,
  Pencil,
  Plus,
  Send,
  Trash2,
  UserMinus,
} from "lucide-react";
import {
  useCanAccess,
  useDataProvider,
  useGetList,
  useGetOne,
  useNotify,
} from "ra-core";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

import { useAgents } from "../useAgents";
import {
  EMAIL_PATH,
  SEGMENTS,
  STATUS_CLASS,
  STATUS_LABEL,
  STYLES,
  formatDateTime,
  percent,
  starterBlocks,
  type EmailCampaign,
  type EmailTemplate,
  type Segment,
} from "./email";
import { DeleteCampaignButton } from "./DeleteCampaignButton";
import { useCampaignFromTemplate } from "./TemplatePage";

type Tab = "campanas" | "plantillas" | "bajas";
const TABS: { key: Tab; label: string; icon: typeof Mail }[] = [
  { key: "campanas", label: "Campañas", icon: Send },
  { key: "plantillas", label: "Plantillas", icon: LayoutTemplate },
  { key: "bajas", label: "Bajas", icon: UserMinus },
];

const segmentLabel = (segment: string) =>
  SEGMENTS.find((item) => item.value === segment)?.label ?? "Todos";

const todayName = () =>
  new Date().toLocaleDateString("es-ES", { day: "numeric", month: "long" });

const CampaignsTab = () => {
  const navigate = useNavigate();
  const { nameOf } = useAgents();
  const {
    data: campaigns = [],
    isPending,
    refetch,
  } = useGetList<EmailCampaign>(
    "gula_email_campaigns_summary",
    {
      pagination: { page: 1, perPage: 200 },
      sort: { field: "created_at", order: "DESC" },
    },
    { refetchInterval: 30_000 },
  );
  if (isPending)
    return <p className="text-sm text-muted-foreground">Cargando…</p>;
  if (!campaigns.length)
    return (
      <div className="flex flex-col items-start gap-1 rounded-3xl border border-dashed p-8">
        <span className="text-lg font-semibold">
          Aún no habéis enviado nada
        </span>
        <span className="text-sm text-muted-foreground">
          Crea la primera campaña arriba: para los que aún no han comprado o
          para los clientes.
        </span>
      </div>
    );
  return (
    <div className="overflow-x-auto rounded-2xl border">
      <table className="w-full text-sm">
        <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
          <tr>
            <th className="px-4 py-2 font-medium">Campaña</th>
            <th className="px-3 py-2 font-medium">Para</th>
            <th className="px-3 py-2 font-medium">Estado</th>
            <th className="px-3 py-2 text-right font-medium">Enviados</th>
            <th className="px-3 py-2 text-right font-medium">Abiertos</th>
            <th className="px-3 py-2 text-right font-medium">Clics</th>
            <th className="px-3 py-2 text-right font-medium">Bajas</th>
            <th className="w-10 px-2 py-2" />
          </tr>
        </thead>
        <tbody className="divide-y">
          {campaigns.map((campaign) => (
            <tr
              key={campaign.id}
              onClick={() => navigate(`${EMAIL_PATH}/${campaign.id}`)}
              className="cursor-pointer hover:bg-muted/40"
            >
              <td className="px-4 py-3">
                <span className="block font-medium">{campaign.name}</span>
                <span className="block max-w-md truncate text-xs text-muted-foreground">
                  {campaign.subject || "Sin asunto"}
                  {" · "}
                  {campaign.status === "draft"
                    ? `editada ${formatDateTime(campaign.updated_at)}`
                    : campaign.status === "scheduled"
                      ? `sale el ${formatDateTime(campaign.scheduled_at)}`
                      : formatDateTime(campaign.started_at)}
                  {campaign.sent_by ? ` · ${nameOf(campaign.sent_by)}` : ""}
                </span>
              </td>
              <td className="px-3 py-3">{segmentLabel(campaign.segment)}</td>
              <td className="px-3 py-3">
                <span
                  className={cn(
                    "rounded-full px-2.5 py-0.5 text-xs font-medium",
                    STATUS_CLASS[campaign.status],
                  )}
                >
                  {STATUS_LABEL[campaign.status]}
                </span>
              </td>
              <td className="px-3 py-3 text-right">
                {campaign.status === "draft"
                  ? "—"
                  : `${campaign.sent ?? 0}${campaign.total && campaign.total !== campaign.sent ? ` / ${campaign.total}` : ""}`}
              </td>
              <td className="px-3 py-3 text-right">
                {campaign.status === "draft"
                  ? "—"
                  : percent(campaign.opened, campaign.sent)}
              </td>
              <td className="px-3 py-3 text-right font-medium">
                {campaign.status === "draft"
                  ? "—"
                  : `${campaign.clicked ?? 0} · ${percent(campaign.clicked, campaign.sent)}`}
              </td>
              <td className="px-3 py-3 text-right">
                {campaign.status === "draft"
                  ? "—"
                  : (campaign.unsubscribed ?? 0)}
              </td>
              <td className="px-2 py-3 text-right">
                <DeleteCampaignButton
                  compact
                  campaign={campaign}
                  onDeleted={() => refetch()}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const TemplatesTab = () => {
  const navigate = useNavigate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const startCampaign = useCampaignFromTemplate();
  const [deleting, setDeleting] = useState<number | null>(null);
  const { data: templates = [], refetch } = useGetList<EmailTemplate>(
    "gula_email_templates",
    {
      pagination: { page: 1, perPage: 200 },
      sort: { field: "updated_at", order: "DESC" },
    },
  );
  const { meId } = useAgents();

  const create = async (template?: EmailTemplate) => {
    try {
      const { data } = await dataProvider.create("gula_email_templates", {
        data: template
          ? {
              name: `${template.name} (copia)`,
              audience: template.audience,
              subject: template.subject,
              preheader: template.preheader ?? "",
              style: template.style,
              blocks: template.blocks,
              created_by: meId ?? null,
            }
          : {
              name: "Nueva plantilla",
              audience: "leads",
              style: "brand",
              blocks: starterBlocks("leads"),
              created_by: meId ?? null,
            },
      });
      navigate(`${EMAIL_PATH}/plantillas/${data.id}`);
    } catch (error) {
      console.error(error);
      notify("No se ha podido crear la plantilla", { type: "error" });
    }
  };

  const remove = async (template: EmailTemplate) => {
    try {
      await dataProvider.delete("gula_email_templates", {
        id: template.id,
        previousData: template,
      });
      setDeleting(null);
      refetch();
    } catch (error) {
      console.error(error);
      notify("No se ha podido borrar", { type: "error" });
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-muted-foreground">
        Diseños listos para reutilizar: la oferta de la semana, el vídeo
        explicativo, el catálogo, las noticias para clientes… Haz el diseño con
        Claude o Canva y móntalo aquí una vez.
      </p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
        <button
          type="button"
          onClick={() => create()}
          className="flex min-h-40 flex-col items-center justify-center gap-2 rounded-2xl border border-dashed text-muted-foreground hover:bg-muted/50 hover:text-foreground"
        >
          <Plus className="h-6 w-6" />
          Nueva plantilla
        </button>
        {templates.map((template) => (
          <div
            key={template.id}
            className="flex min-h-40 flex-col gap-2 rounded-2xl border p-4"
          >
            <div className="flex items-start justify-between gap-2">
              <button
                type="button"
                onClick={() =>
                  navigate(`${EMAIL_PATH}/plantillas/${template.id}`)
                }
                className="text-left text-base font-semibold hover:underline"
              >
                {template.name}
              </button>
              <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-xs">
                {template.audience === "all"
                  ? "Todos"
                  : segmentLabel(template.audience)}
              </span>
            </div>
            <p className="line-clamp-2 text-sm text-muted-foreground">
              {template.subject || "Sin asunto"}
            </p>
            <p className="text-xs text-muted-foreground">
              {STYLES.find((style) => style.value === template.style)?.label} ·{" "}
              {template.blocks.length} bloques ·{" "}
              {formatDateTime(template.updated_at)}
            </p>
            <div className="mt-auto flex flex-wrap items-center gap-1.5">
              <Button
                size="sm"
                className="bg-lime-400 text-neutral-900 hover:bg-lime-300"
                onClick={() => startCampaign(template)}
              >
                <Send className="h-3.5 w-3.5" />
                Usar
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() =>
                  navigate(`${EMAIL_PATH}/plantillas/${template.id}`)
                }
              >
                <Pencil className="h-3.5 w-3.5" />
                Editar
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => create(template)}
              >
                <Copy className="h-3.5 w-3.5" />
                Duplicar
              </Button>
              {deleting === template.id ? (
                <span className="ml-auto flex items-center gap-2 text-xs">
                  <button
                    type="button"
                    className="font-medium text-destructive hover:underline"
                    onClick={() => remove(template)}
                  >
                    Sí, borrar
                  </button>
                  <button
                    type="button"
                    className="hover:underline"
                    onClick={() => setDeleting(null)}
                  >
                    No
                  </button>
                </span>
              ) : (
                <Button
                  size="icon"
                  variant="ghost"
                  className="ml-auto h-8 w-8 text-muted-foreground hover:text-destructive"
                  onClick={() => setDeleting(template.id)}
                  title="Borrar"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const UnsubscribesTab = () => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [email, setEmail] = useState("");
  const { data: rows = [], refetch } = useGetList<{
    id: number;
    email: string;
    created_at: string;
    source: string;
  }>("gula_email_unsubscribes", {
    pagination: { page: 1, perPage: 5000 },
    sort: { field: "created_at", order: "DESC" },
  });

  const add = async () => {
    const value = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value)) {
      notify("Escribe un email válido", { type: "warning" });
      return;
    }
    try {
      await dataProvider.create("gula_email_unsubscribes", {
        data: { email: value, source: "manual" },
      });
      setEmail("");
      refetch();
    } catch {
      notify("Ese email ya estaba de baja", { type: "info" });
    }
  };

  const remove = async (row: { id: number; email: string }) => {
    try {
      await dataProvider.delete("gula_email_unsubscribes", {
        id: row.id,
        previousData: row,
      });
      refetch();
      notify(`${row.email} vuelve a recibir correos`, { type: "info" });
    } catch (error) {
      console.error(error);
      notify("No se ha podido", { type: "error" });
    }
  };

  return (
    <div className="flex max-w-2xl flex-col gap-4">
      <p className="text-sm text-muted-foreground">
        Nadie de esta lista recibe correos de campañas (los avisos de su pedido
        sí). Se apuntan solos al pulsar «date de baja»; si alguien te lo pide
        por teléfono o WhatsApp, añádelo aquí. Por ley no se le puede volver a
        escribir sin su permiso.
      </p>
      <div className="flex gap-2">
        <Input
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          onKeyDown={(event) => event.key === "Enter" && add()}
          placeholder="email@ejemplo.com"
          className="max-w-xs"
        />
        <Button variant="outline" onClick={add}>
          Dar de baja
        </Button>
      </div>
      <ul className="flex flex-col divide-y rounded-xl border">
        {rows.map((row) => (
          <li
            key={row.email}
            className="flex items-center gap-3 px-3 py-2 text-sm"
          >
            <span className="flex-1 truncate font-medium">{row.email}</span>
            <span className="text-xs text-muted-foreground">
              {row.source === "manual" ? "a mano" : "desde el correo"} ·{" "}
              {formatDateTime(row.created_at)}
            </span>
            <button
              type="button"
              className="text-xs text-muted-foreground hover:text-foreground hover:underline"
              onClick={() => remove(row)}
              title="Solo si te ha pedido volver a recibirlos"
            >
              Quitar
            </button>
          </li>
        ))}
        {!rows.length ? (
          <li className="px-3 py-6 text-center text-sm text-muted-foreground">
            Nadie se ha dado de baja.
          </li>
        ) : null}
      </ul>
    </div>
  );
};

/** Hourly sending limit (IONOS); admins can raise it as the mailbox ages. */
const SpeedSetting = () => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const { canAccess: isAdmin } = useCanAccess({
    resource: "money",
    action: "list",
  });
  const { data, refetch } = useGetOne<{ id: number; hourly_limit: number }>(
    "gula_email_settings",
    { id: 1 },
  );
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");
  if (!data) return null;
  const save = async () => {
    const limit = Math.round(Number(value));
    if (!limit || limit < 1 || limit > 500) {
      notify("Entre 1 y 500 correos por hora", { type: "warning" });
      return;
    }
    await dataProvider.update("gula_email_settings", {
      id: 1,
      data: { hourly_limit: limit },
      previousData: data,
    });
    setEditing(false);
    refetch();
  };
  return (
    <p className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
      Salen como máximo <strong>{data.hourly_limit} correos por hora</strong>{" "}
      desde notificaciones@guladeagua.es (IONOS deja 100/h el primer mes del
      buzón, 400/h desde el día 15 y 500/h después).
      {isAdmin && !editing ? (
        <button
          type="button"
          className="underline"
          onClick={() => {
            setValue(String(data.hourly_limit));
            setEditing(true);
          }}
        >
          Cambiar
        </button>
      ) : null}
      {editing ? (
        <span className="flex items-center gap-1">
          <Input
            type="number"
            value={value}
            onChange={(event) => setValue(event.target.value)}
            className="h-7 w-20 text-xs"
          />
          <Button size="sm" className="h-7" onClick={save}>
            Guardar
          </Button>
        </span>
      ) : null}
    </p>
  );
};

/** /correo — email marketing to leads and customers. */
export const EmailPage = () => {
  const dataProvider = useDataProvider();
  const navigate = useNavigate();
  const notify = useNotify();
  const { meId } = useAgents();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = (searchParams.get("tab") as Tab) || "campanas";

  const newCampaign = async (segment: Segment) => {
    try {
      const { data } = await dataProvider.create("gula_email_campaigns", {
        data: {
          name: `${segment === "clients" ? "Clientes" : "Leads"} · ${todayName()}`,
          segment,
          style: "brand",
          subject: "",
          blocks: starterBlocks(segment),
          created_by: meId ?? null,
        },
      });
      navigate(`${EMAIL_PATH}/${data.id}`);
    } catch (error) {
      console.error(error);
      notify("No se ha podido crear la campaña", { type: "error" });
    }
  };

  return (
    <div className="flex flex-col gap-6 py-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-3xl font-semibold tracking-tight">
            <Mail className="h-7 w-7" />
            Correo
          </h1>
          <p className="text-sm text-muted-foreground">
            Campañas por email a los leads y a los clientes, con aperturas,
            clics y bajas.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {SEGMENTS.map((segment) => (
            <Button
              key={segment.value}
              onClick={() => newCampaign(segment.value)}
              variant={segment.value === "leads" ? "default" : "outline"}
              className={cn(
                segment.value === "leads" &&
                  "bg-lime-400 text-neutral-900 hover:bg-lime-300",
              )}
            >
              <Plus className="h-4 w-4" />
              Campaña · {segment.label.toLowerCase()}
            </Button>
          ))}
        </div>
      </div>

      <div className="flex gap-1 rounded-full bg-muted p-1 sm:w-fit">
        {TABS.map((item) => (
          <button
            key={item.key}
            type="button"
            onClick={() =>
              setSearchParams(
                item.key === "campanas" ? {} : { tab: item.key },
                { replace: true },
              )
            }
            className={cn(
              "flex flex-1 items-center justify-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-medium transition-colors sm:flex-none",
              tab === item.key
                ? "bg-background shadow-sm"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            <item.icon className="h-4 w-4" />
            {item.label}
          </button>
        ))}
      </div>

      {tab === "campanas" ? <CampaignsTab /> : null}
      {tab === "plantillas" ? <TemplatesTab /> : null}
      {tab === "bajas" ? <UnsubscribesTab /> : null}

      <SpeedSetting />
    </div>
  );
};

EmailPage.path = EMAIL_PATH;
